import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'gatsby-plugin-intl'
import translate from 'src/intl/translate'
import { breakpoint } from 'src/theme/grid'
import useClickOutside from 'src/hooks/useClickOutside'
import useFilterDropdownBoundary from 'src/hooks/useFilterDropdownBoundary'
import DatePicker from 'src/components/atoms/DatePicker'
import TextInputMasked from 'src/components/atoms/TextInputMasked'
import {
  FilterBox,
  FilterDropdown,
  SelectOptionsWrapper,
  SelectRow
} from 'src/components/filters/components/atoms'
import {
  completeOfferDateRange,
  createOfferDatePreset,
  formatOfferDateRange,
  getOfferDateRangeFields,
  getOfferDateRangeFromFields
} from './offerAgeFilter'

const EMPTY_RANGE = {
  fields: getOfferDateRangeFields('all'),
  range: 'all'
}

export const OfferAgeFilter = ({
  alignRight = false,
  className,
  dropdownBoundaryRef,
  id,
  range,
  onChange,
  onDropdownClose,
  stackDateTime = false
}) => {
  const { locale } = useIntl()
  const intl = translate(locale)
  const [opened, setOpened] = useState(false)
  const [draftRange, setDraftRange] = useState(EMPTY_RANGE)
  const [invalid, setInvalid] = useState(false)
  const edited = useRef(false)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const wasOpened = useRef(false)
  const componentRef = useClickOutside(() => setOpened(false))
  const dropdownStyle = useFilterDropdownBoundary(
    opened,
    componentRef,
    dropdownBoundaryRef
  )
  useEffect(() => {
    if (wasOpened.current && !opened && onDropdownClose) onDropdownClose()
    wasOpened.current = opened
  }, [opened, onDropdownClose])
  useEffect(() => {
    edited.current = false
    setDraftRange(previous =>
      previous.range &&
      previous.range.from === range?.from &&
      previous.range.to === range?.to
        ? previous
        : { fields: getOfferDateRangeFields(range), range }
    )
  }, [range?.from, range?.to])
  useEffect(() => {
    if (!edited.current) return undefined
    const completed = draftRange.range
      ? draftRange
      : completeOfferDateRange(draftRange.fields)
    if (!completed.range) return undefined
    if (
      completed.range === range ||
      (completed.range.from === range?.from && completed.range.to === range?.to)
    ) {
      edited.current = false
      return undefined
    }
    const timeout = setTimeout(() => {
      if (!edited.current) return
      edited.current = false
      onChangeRef.current(completed.range)
    }, 700)
    return () => clearTimeout(timeout)
  }, [draftRange, range?.from, range?.to])
  const label = intl.formatMessage({ id: 'offersFilters.createdAtWindow' })
  const options = [
    {
      value: '15min',
      label: intl.formatMessage({ id: 'offersFilters.createdAtLast15Minutes' }),
      minutes: 15
    },
    {
      value: '1h',
      label: intl.formatMessage({ id: 'offersFilters.createdAtLast1Hour' }),
      minutes: 60
    },
    {
      value: '24h',
      label: intl.formatMessage({ id: 'offersFilters.createdAtLast24Hours' }),
      minutes: 1440
    }
  ]
  const caption = formatOfferDateRange(range, intl) || label

  const toggleDropdown = () => {
    if (!opened) {
      edited.current = false
      const appliedRange =
        draftRange.range?.preset &&
        range !== 'all' &&
        draftRange.range?.from === range.from &&
        draftRange.range?.to === range.to
          ? { ...range, preset: draftRange.range.preset }
          : range
      setDraftRange({
        fields: getOfferDateRangeFields(range),
        range: appliedRange
      })
      setInvalid(false)
    }
    setOpened(!opened)
  }
  const clear = event => {
    edited.current = false
    if (event) event.stopPropagation()
    onChange('all')
    setDraftRange(EMPTY_RANGE)
    setInvalid(false)
  }
  const selectOption = option => {
    edited.current = true
    const presetRange = {
      ...createOfferDatePreset(option.minutes),
      preset: option.value
    }
    setDraftRange({
      fields: getOfferDateRangeFields(presetRange),
      range: presetRange
    })
    setInvalid(false)
  }
  const updateRange = (name, value, inputValue) => {
    edited.current = true
    let fieldValue =
      name.endsWith('Time') && !/\d/.test(value || '') ? '' : value || ''
    // The picker can parse an unfinished year as a valid date.
    if (
      name.endsWith('Date') &&
      inputValue &&
      !/^\d{2}\.\d{2}\.\d{4}$/.test(inputValue)
    ) {
      fieldValue = 'Invalid date'
    }
    setDraftRange(previous => {
      const fields = { ...previous.fields, [name]: fieldValue }
      return { fields, range: getOfferDateRangeFromFields(fields) }
    })
    setInvalid(false)
  }
  const confirm = () => {
    edited.current = false
    const completedDraft = draftRange.range
      ? draftRange
      : completeOfferDateRange(draftRange.fields)
    setDraftRange(completedDraft)
    if (!completedDraft.range) {
      setInvalid(true)
      return
    }
    if (
      completedDraft.range !== range &&
      (completedDraft.range.from !== range?.from ||
        completedDraft.range.to !== range?.to)
    ) {
      onChange(completedDraft.range)
    }
    setOpened(false)
  }

  return (
    <FilterWrapper className={className} id={id} ref={componentRef}>
      <FilterTrigger
        opened={opened}
        hasValue={range !== 'all'}
        onClick={toggleDropdown}
        onClear={clear}
      >
        <span title={caption}>{caption}</span>
      </FilterTrigger>
      {opened && (
        <AgeDropdown
          alignRight={alignRight}
          style={dropdownStyle}
          stickyActions
          filterName={label}
          onClose={() => setOpened(false)}
          onConfirm={confirm}
          onClear={clear}
          clearButtonText={intl.formatMessage({ id: 'offersFilters.clear' })}
          confirmButtonText={intl.formatMessage({
            id: 'offersFilters.confirm'
          })}
        >
          <SelectOptionsWrapper>
            {options.map(option => (
              <SelectRow
                key={option.value}
                type='radio'
                selected={Boolean(
                  draftRange.range && draftRange.range.preset === option.value
                )}
                name={option.label}
                onClick={() => selectOption(option)}
              />
            ))}
            <RangeForm
              onSubmit={event => {
                event.preventDefault()
                confirm()
              }}
            >
              <RangeFields>
                {['from', 'to'].map(name => {
                  const fieldLabel = intl.formatMessage({
                    id:
                      name === 'from'
                        ? 'offersFilters.createdAtFrom'
                        : 'offersFilters.createdAtTo'
                  })
                  return (
                    <Field key={name}>
                      <legend>{fieldLabel}</legend>
                      <DateTimeFields stacked={stackDateTime}>
                        <DatePicker
                          id={`${id}-${name}-date`}
                          name={`${name}Date`}
                          label={intl.formatMessage({
                            id: 'offersFilters.createdAtDate'
                          })}
                          placeholder='dd.mm.yyyy'
                          value={draftRange.fields[`${name}Date`] || null}
                          onChange={updateRange}
                          popoverContainer={() => componentRef.current}
                          pristine
                        />
                        <TextInputMasked
                          id={`${id}-${name}-time`}
                          name={`${name}Time`}
                          label={intl.formatMessage({
                            id: 'offersFilters.createdAtTime'
                          })}
                          mask='99:99'
                          type='tel'
                          placeholder='HH:mm'
                          calendarTime
                          value={draftRange.fields[`${name}Time`]}
                          onChange={updateRange}
                        />
                      </DateTimeFields>
                    </Field>
                  )
                })}
              </RangeFields>
              {invalid && (
                <RangeError role='alert'>
                  {intl.formatMessage({
                    id: 'offersFilters.createdAtInvalidRange'
                  })}
                </RangeError>
              )}
              <HiddenSubmit type='submit' tabIndex={-1} aria-hidden='true' />
            </RangeForm>
          </SelectOptionsWrapper>
        </AgeDropdown>
      )}
    </FilterWrapper>
  )
}

const FilterWrapper = styled.div`
  display: flex;
  position: relative;
`
const FilterTrigger = styled(FilterBox)`
  width: 100%;
  ${breakpoint.m`width: 180px;`}
`
const AgeDropdown = styled(FilterDropdown)`
  ${breakpoint.m`
    width: 360px;
    max-width: calc(100vw - 32px);
    height: 480px;
    max-height: calc(100vh - 80px);
  `}
`
const RangeForm = styled.form`
  padding: 0.6rem 0.8rem;
`
const RangeFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 0.6rem;
`
const Field = styled.fieldset`
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  font-size: 0.85rem;
  legend {
    padding: 0;
    margin-bottom: 0.4rem;
    font-weight: 600;
  }
`
const DateTimeFields = styled.div`
  display: grid;
  grid-template-columns: ${({ stacked }) =>
    stacked ? 'minmax(0, 1fr)' : 'minmax(0, 1.6fr) minmax(0, 1fr)'};
  gap: 0.6rem;
  > div > span:last-child:empty {
    display: none;
  }
`
const RangeError = styled.p`
  margin: 0.6rem 0 0;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.error.hex()};
`
const HiddenSubmit = styled.button`
  display: none;
`

export const OfferAgeFilterCount = ({ count }) => (
  <>
    <span>{`${count} `}</span>
    <FormattedMessage id='offers.offersFound' values={{ count }} />
  </>
)
